import { Box, CodeBlock, Icon, Text } from '@customerio/pluma-components/react';
import * as styles from './bash-detail.css';

interface BashResponse {
  stdout?: string;
  stderr?: string;
  exit_code?: number;
}

interface BashDetailProps {
  /** The bash command that was executed. */
  command: string;
  /** The response from the bash tool. */
  response?: BashResponse | null;
}

export function BashDetail({ command, response }: BashDetailProps) {
  // Skip approval-only responses (e.g. { status: "approved" }) — they don't have bash output.
  const hasBashOutput = response != null && ('stdout' in response || 'stderr' in response);
  const stderr = hasBashOutput ? response.stderr?.trim() ?? '' : '';
  const stdout = hasBashOutput ? response.stdout?.trim() ?? '' : '';
  const isError =
    response != null &&
    ((response.exit_code != null && response.exit_code !== 0) ||
      (response.exit_code == null && stderr.length > 0 && stdout.length === 0));
  // Show stderr on error, stdout on success. When both are present on success, prefer stdout.
  const output = isError && stderr.length > 0 ? stderr : stdout.length > 0 ? stdout : stderr;
  const hasOutput = output.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap="100">
      <Box className={styles.codeWrapper}>
        <CodeBlock code={command} language="bash" mode="dark" withCopyButton={true} />
      </Box>
      {hasOutput ? (
        <Box display="flex" flexDirection="column" gap="050">
          <Box display="flex" alignItems="center" gap="050">
            {isError ? <Icon name="error" size="xs" color="critical" /> : null}
            <Text size="xs" weight="medium" color={isError ? 'critical' : 'subtle'}>
              {isError ? 'Error' : 'Output'}
            </Text>
          </Box>
          <Box className={styles.outputWrapper}>
            <CodeBlock code={output} language="plaintext" mode="dark" withCopyButton={true} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
